import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import type { ChatMessage, ChatOut } from '../types/ChatMessage'
import { formatMessageGroupDate } from '@/utils/russianDateFormatter'

interface MessageGroup {
  senderId: ChatOut['messages'][number]['senderId']
  messages: ChatOut['messages'][number][]
}

export type ChatItem =
  | { kind: 'date'; label: string; dateKey: string }
  | { kind: 'group'; group: MessageGroup }
  | { kind: 'system'; message: ChatMessage }

export const useMessageGrouping = (
  messages: ComputedRef<ChatOut['messages']>,
) => {
  const getDateKey = (iso: string): string => {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return 'unknown'
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')

    return `${y}-${m}-${day}`
  }

  const groupedMessages = computed<ChatItem[]>(() => {
    const msgs = messages.value

    if (!msgs || !msgs.length) return []

    interface Section {
      dateKey: string
      label: string
      msgs: typeof msgs
    }
    const sections: Section[] = []

    let currentKey = getDateKey(msgs[0].time)
    let currentSection: Section = {
      dateKey: currentKey,
      label: formatMessageGroupDate(msgs[0].time),
      msgs: [],
    }

    for (const m of msgs) {
      const key = getDateKey(m.time)
      if (key !== currentKey) {
        sections.push(currentSection)
        currentKey = key
        currentSection = {
          dateKey: key,
          label: formatMessageGroupDate(m.time),
          msgs: [],
        }
      }
      currentSection.msgs.push(m)
    }

    sections.push(currentSection)

    const result: ChatItem[] = []

    for (const section of sections) {
      result.push({
        kind: 'date',
        label: section.label,
        dateKey: section.dateKey,
      })

      if (!section.msgs.length) continue

      let currentSender: ChatMessage['senderId'] | null = null
      let group: MessageGroup | null = null

      const flushGroup = () => {
        if (group && group.messages.length) {
          result.push({ kind: 'group', group })
        }
        group = null
        currentSender = null
      }

      for (const msg of section.msgs) {
        if (msg.is_system) {
          flushGroup()
          result.push({ kind: 'system', message: msg })
          continue
        }

        if (!group || msg.senderId !== currentSender) {
          flushGroup()
          currentSender = msg.senderId
          group = { senderId: currentSender, messages: [msg] }
          continue
        }

        group.messages.push(msg)
      }

      flushGroup()
    }

    return result
  })

  return {
    groupedMessages,
  }
}
